
import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Form, Row, Col, Modal, Badge, Spinner } from "react-bootstrap";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";
import { formatDateTime } from "../utils/format";

/**
 * Lists registration requests from:
 *   admin (collection) / regrequest (doc) / requests (subcollection)
 */
export default function RequestsList() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewItem, setViewItem] = useState(null);
  const [search, setSearch] = useState("");
  const [statusUpdating, setStatusUpdating] = useState(null);

  useEffect(() => {
    const anchorDocRef = doc(db, "admin", "regrequest");
    const requestsColRef = collection(anchorDocRef, "requests");

    // Latest first; throttle to ~200 for admin view, tweak as needed
    const q = query(requestsColRef, orderBy("createdAt", "desc"), limit(200));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(list);
        setLoading(false);
      },
      (err) => {
        console.error("onSnapshot error (requests):", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [
        r.communityName,
        r.city,
        r.contactName,
        r.email,
        r.phone,
        r.status,
        r.uid,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [rows, search]);

  async function updateStatus(id, nextStatus) {
    try {
      setStatusUpdating(id);
      const ref = doc(db, "admin", "regrequest", "requests", id);
      await updateDoc(ref, {
        status: nextStatus,
        updatedAt: new Date(),
      });
    } catch (e) {
      console.error("Failed to update status:", e);
      alert("Failed to update status. See console for details.");
    } finally {
      setStatusUpdating(null);
    }
  }

  return (
    <>
      <Row className="mb-3">
        <Col>
          <h4 className="mb-0">Registration Requests</h4>
          <small className="text-muted">Latest enquiries submitted by visitors</small>
        </Col>
        <Col md="auto">
          <Form.Control
            placeholder="Search by name, email, phone, city…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
      </Row>

      {loading ? (
        <div className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" /> <span>Loading…</span>
        </div>
      ) : filtered.length === 0 ? (
        <div className="alert alert-info">No requests found.</div>
      ) : (
        <Table hover responsive className="align-middle">
          <thead>
            <tr>
              <th>Community</th>
              <th>City</th>
              <th>Total Flats</th>
              <th>Contact</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Created</th>
              <th style={{ width: 220 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id}>
                <td>{r.communityName || "-"}</td>
                <td>{r.city || "-"}</td>
                <td>{r.totalFlats ?? "-"}</td>
                <td>{r.contactName || "-"}</td>
                <td>{r.email || "-"}</td>
                <td>{r.phone || "-"}</td>
                <td>
                  <Badge bg={badgeForStatus(r.status)}>{r.status || "new"}</Badge>
                </td>
                <td>{formatDateTime(r.createdAt)}</td>
                <td className="d-flex flex-wrap gap-2">
                  <Button size="sm" variant="outline-primary" onClick={() => setViewItem(r)}>
                    View
                  </Button>

                  <Button
                    size="sm"
                    variant="outline-success"
                    disabled={statusUpdating === r.id}
                    onClick={() => updateStatus(r.id, "contacted")}
                  >
                    {statusUpdating === r.id ? "Updating…" : "Mark Contacted"}
                  </Button>

                  <Button
                    size="sm"
                    variant="outline-dark"
                    disabled={statusUpdating === r.id}
                    onClick={() => updateStatus(r.id, "onboarded")}
                  >
                    {statusUpdating === r.id ? "Updating…" : "Mark Onboarded"}
                  </Button>

                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={statusUpdating === r.id}
                    onClick={() => updateStatus(r.id, "rejected")}
                  >
                    {statusUpdating === r.id ? "Updating…" : "Reject"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <RequestViewModal item={viewItem} onHide={() => setViewItem(null)} />
    </>
  );
}

function badgeForStatus(s) {
  switch ((s || "").toLowerCase()) {
    case "new":
      return "secondary";
    case "contacted":
      return "info";
    case "onboarded":
      return "success";
    case "rejected":
      return "danger";
    default:
      return "secondary";
  }
}

function RequestViewModal({ item, onHide }) {
  const show = !!item;
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Request Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!item ? (
          <div className="text-muted">No data</div>
        ) : (
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="text-muted d-block">Community Name</label>
              <div className="fw-semibold">{item.communityName || "-"}</div>
            </div>
            <div className="col-12 col-md-3">
              <label className="text-muted d-block">City</label>
              <div className="fw-semibold">{item.city || "-"}</div>
            </div>
            <div className="col-12 col-md-3">
              <label className="text-muted d-block">Total Flats</label>
              <div className="fw-semibold">{item.totalFlats ?? "-"}</div>
            </div>

            <div className="col-12 col-md-4">
              <label className="text-muted d-block">Contact Person</label>
              <div className="fw-semibold">{item.contactName || "-"}</div>
            </div>
            <div className="col-12 col-md-4">
              <label className="text-muted d-block">Email</label>
              <div className="fw-semibold">{item.email || "-"}</div>
            </div>
            <div className="col-12 col-md-4">
              <label className="text-muted d-block">Phone</label>
              <div className="fw-semibold">{item.phone || "-"}</div>
            </div>

            <div className="col-12 col-md-3">
              <label className="text-muted d-block">Status</label>
              <div>
                <Badge bg={badgeForStatus(item.status)}>{item.status || "new"}</Badge>
              </div>
            </div>
            <div className="col-12 col-md-4">
              <label className="text-muted d-block">UID / Ref</label>
              <div className="fw-semibold">{item.uid || item.id}</div>
            </div>
            <div className="col-12 col-md-5">
              <label className="text-muted d-block">Created At</label>
              <div className="fw-semibold">{formatDateTime(item.createdAt)}</div>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
